import { ChangeEvent, FC } from 'react'
import styled from 'styled-components'
import { Controller } from 'react-hook-form'
import { StyledInput } from 'Components/Inputs/StyledInput'
import { normalizeAmountInput, pasteAmountInto } from 'Components/Inputs/FormAmountInput'
import { StyledInputLabel } from 'Components/Inputs/StyledInputLabel'
import { ControlledStyledSelect } from 'Components/Inputs/ControlledStyledSelect'
import { InputContainer } from 'Components/Inputs/ControlledSelect'
import { FormDatePicker } from 'Components/Inputs/Calendar/FormDatePicker'
import { ControlledSwitch } from 'Components/Inputs/Switch/ControlledSwitch'
import { StyledButton } from 'Components/Buttons/StyledButton'
import { FlexRow, spacingMD, spacingLG, ButtonContainer } from 'Utils/styles/spacing'
import { CountryCode, CountryNames } from 'Utils/types/countries'
import { chargeFeeTypeOptions, FeeSchedule } from 'Utils/types/employerInfo'
import { fontSizeMD, fontWeightBold } from 'Utils/styles/text'
import { isAmount } from 'Utils/helpers/currency'
import { useFeeScheduleForm } from './useFeeScheduleForm'

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacingMD};
`

const Title = styled.div`
  font-size: ${fontSizeMD};
  font-weight: ${fontWeightBold};
`

type FeeScheduleFormProps = {
  closeModal: () => void
  countryCode: CountryCode
  feeSchedule?: FeeSchedule | null
}

export const FeeScheduleForm: FC<FeeScheduleFormProps> = ({
  closeModal,
  countryCode,
  feeSchedule
}) => {
  const { control, handleSubmit, handleSubmission, handleError, isEdit, isLoading } =
    useFeeScheduleForm(closeModal, countryCode, feeSchedule)

  const now = new Date()
  const minDate = new Date(now.getFullYear(), now.getMonth(), 1)

  return (
    <FormContainer onSubmit={handleSubmit(handleSubmission, handleError)}>
      <Title>
        {isEdit ? 'Edit' : 'Add'} fee schedule: {CountryNames[countryCode]}
      </Title>

      <InputContainer>
        <StyledInputLabel>Effective month</StyledInputLabel>
        <Controller
          control={control}
          name="startsOn"
          render={({ field: { onChange, value } }) => (
            <FormDatePicker
              date={value}
              onChange={onChange}
              minDate={minDate}
              disabled={isEdit}
              calenderSubText="The change takes effect on the 1st of the selected month."
            />
          )}
        />
      </InputContainer>

      <FlexRow gap={spacingLG}>
        <InputContainer>
          <StyledInputLabel>Effective fee</StyledInputLabel>
          <Controller
            control={control}
            name="processingFee"
            rules={{
              validate: (value) => isAmount(value) || 'A valid effective fee is required.'
            }}
            render={({ field: { onChange, value } }) => (
              <StyledInput
                onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(normalizeAmountInput(e))}
                onPaste={pasteAmountInto(onChange)}
                value={value}
              />
            )}
          />
        </InputContainer>
        <InputContainer>
          <StyledInputLabel>Base fee (optional)</StyledInputLabel>
          <Controller
            control={control}
            name="baseProcessingFee"
            rules={{
              validate: (value) => `${value ?? ''}` === '' || isAmount(value)
            }}
            render={({ field: { onChange, value } }) => (
              <StyledInput
                onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(normalizeAmountInput(e))}
                onPaste={pasteAmountInto(onChange)}
                value={value ?? ''}
              />
            )}
          />
        </InputContainer>
      </FlexRow>

      <InputContainer>
        <ControlledStyledSelect
          label="Charge Fee Per"
          name="chargeFeePer"
          options={chargeFeeTypeOptions}
          control={control}
        />
      </InputContainer>

      <ControlledSwitch
        label="Alternative Reimbursement"
        name="alternativeReimbursement"
        control={control}
        disabled={isEdit}
      />

      <ButtonContainer>
        <StyledButton primary id="save.label" isLoading={isLoading} />
      </ButtonContainer>
    </FormContainer>
  )
}
